/**
 * Homepage hero intake: business problems → outcomes → recommended channels → plan.
 * Canonical IDs persist through /onboard; display labels are derived, not stored alone.
 */

import {
  AI_EMPLOYEE_PLANS,
  formatAiEmployeePlanPriceLabel,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";
import {
  HERO_DEPLOY_CHANNEL_OPTIONS,
  minimumPlanForChannels,
  normalizeDeployChannels,
  parseChannelsFromParam,
  serializeChannelsParam,
  type HeroDeployChannelId,
} from "~/lib/siteglue/plan-feature-gates";
import { parsePricingServices } from "~/lib/siteglue/pricing-configurator";

export type BusinessProblem =
  | "answer_questions"
  | "capture_leads"
  | "schedule_appointments"
  | "answer_missed_calls"
  | "after_hours_support"
  | "qualify_prospects"
  | "reply_to_messages"
  | "route_to_team";

export type HomepageBusinessProblemOption = {
  id: BusinessProblem;
  label: string;
  /** Short chip label for dense hero UI. */
  shortLabel: string;
  outcome: string;
  recommendedChannels: HeroDeployChannelId[];
};

export const HOMEPAGE_BUSINESS_PROBLEMS: readonly HomepageBusinessProblemOption[] = [
  {
    id: "answer_questions",
    label: "Online support",
    shortLabel: "Online support",
    outcome: "Improve customer access and reduce repetitive staff work",
    recommendedChannels: ["Website Chat", "Web Voice"],
  },
  {
    id: "capture_leads",
    label: "Capture new leads",
    shortLabel: "Capture leads",
    outcome: "Increase lead capture and conversion opportunities",
    recommendedChannels: ["Website Chat", "Phone Calls", "SMS", "Email"],
  },
  {
    id: "schedule_appointments",
    label: "Schedule appointments",
    shortLabel: "Schedule appointments",
    outcome: "Increase appointment requests and reduce scheduling friction",
    recommendedChannels: ["Website Chat", "Phone Calls", "SMS", "Email"],
  },
  {
    id: "answer_missed_calls",
    label: "Answer missed calls",
    shortLabel: "Answer missed calls",
    outcome: "Reduce lost opportunities from unanswered calls",
    recommendedChannels: ["Phone Calls"],
  },
  {
    id: "after_hours_support",
    label: "Provide after-hours support",
    shortLabel: "After-hours support",
    outcome: "Engage visitors and callers outside business hours",
    recommendedChannels: ["Website Chat", "Phone Calls", "SMS"],
  },
  {
    id: "qualify_prospects",
    label: "Qualify prospects",
    shortLabel: "Qualify prospects",
    outcome: "Identify high-intent prospects and collect relevant details",
    recommendedChannels: ["Website Chat", "Phone Calls", "Email"],
  },
  {
    id: "reply_to_messages",
    label: "Reply to messages",
    shortLabel: "Reply to messages",
    outcome: "Improve response speed across communication channels",
    recommendedChannels: ["SMS", "Email", "WhatsApp", "Messenger"],
  },
  {
    id: "route_to_team",
    label: "Route conversations to our team",
    shortLabel: "Route to our team",
    outcome: "Connect customers with the correct human when needed",
    recommendedChannels: ["Website Chat", "Phone Calls", "SMS", "Email"],
  },
] as const;

export const DEFAULT_HOMEPAGE_PROBLEM: BusinessProblem = "answer_questions";

export const HOMEPAGE_INTAKE_STORAGE_KEY = "siteglue-homepage-intake-v1";

export type HomepageOnboardingIntake = {
  websiteUrl: string | null;
  selectedProblems: BusinessProblem[];
  desiredOutcomes: string[];
  selectedChannels: string[];
  recommendedPlan: Exclude<AiEmployeePlanId, "enterprise">;
  source: "homepage_hero";
  services?: string[];
  wordpressDetected?: boolean;
  totalCents?: number | null;
};

const PROBLEM_IDS = new Set(
  HOMEPAGE_BUSINESS_PROBLEMS.map((p) => p.id),
);

export function isBusinessProblem(value: string): value is BusinessProblem {
  return PROBLEM_IDS.has(value as BusinessProblem);
}

export function parseProblemsFromParam(raw: string | null | undefined): BusinessProblem[] {
  if (!raw?.trim()) return [];
  const out: BusinessProblem[] = [];
  for (const part of raw.split(",")) {
    const id = decodeURIComponent(part.trim());
    if (isBusinessProblem(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

export function serializeProblemsParam(problems: BusinessProblem[]): string {
  return problems.filter(isBusinessProblem).join(",");
}

export function outcomesForProblems(problems: BusinessProblem[]): string[] {
  return HOMEPAGE_BUSINESS_PROBLEMS.filter((p) => problems.includes(p.id)).map(
    (p) => p.outcome,
  );
}

export function labelsForProblems(problems: BusinessProblem[]): string[] {
  return HOMEPAGE_BUSINESS_PROBLEMS.filter((p) => problems.includes(p.id)).map(
    (p) => p.shortLabel,
  );
}

/** Union of system-recommended channels for the selected problems. */
export function recommendedChannelsForProblems(
  problems: BusinessProblem[],
): HeroDeployChannelId[] {
  const set = new Set<HeroDeployChannelId>();
  for (const problem of HOMEPAGE_BUSINESS_PROBLEMS) {
    if (!problems.includes(problem.id)) continue;
    for (const ch of problem.recommendedChannels) set.add(ch);
  }
  if (set.size === 0) set.add("Website Chat");
  // Preserve catalog order
  return HERO_DEPLOY_CHANNEL_OPTIONS.map((o) => o.id).filter((id) => set.has(id));
}

/**
 * Merge newly recommended channels into the current selection without
 * re-adding channels the user explicitly deselected.
 */
export function mergeRecommendedChannels(opts: {
  current: string[];
  recommended: string[];
  userDeselected: ReadonlySet<string>;
}): HeroDeployChannelId[] {
  const current = new Set(
    normalizeDeployChannels(opts.current) as HeroDeployChannelId[],
  );
  for (const ch of normalizeDeployChannels(opts.recommended)) {
    if (opts.userDeselected.has(ch)) continue;
    current.add(ch as HeroDeployChannelId);
  }
  const ordered = HERO_DEPLOY_CHANNEL_OPTIONS.map((o) => o.id).filter((id) =>
    current.has(id),
  );
  return ordered.length ? ordered : ["Website Chat"];
}

export function buildHomepageOnboardingIntake(opts: {
  websiteUrl: string | null;
  selectedProblems: BusinessProblem[];
  selectedChannels: string[];
}): HomepageOnboardingIntake {
  const problems = opts.selectedProblems.filter(isBusinessProblem);
  const channels = normalizeDeployChannels(opts.selectedChannels);
  return {
    websiteUrl: opts.websiteUrl?.trim() || null,
    selectedProblems: problems,
    desiredOutcomes: outcomesForProblems(problems),
    selectedChannels: channels,
    recommendedPlan: minimumPlanForChannels(channels),
    source: "homepage_hero",
  };
}

export function writeHomepageIntake(intake: HomepageOnboardingIntake): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(HOMEPAGE_INTAKE_STORAGE_KEY, JSON.stringify(intake));
  } catch {
    /* ignore quota / private mode */
  }
}

export function readHomepageIntake(): HomepageOnboardingIntake | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(HOMEPAGE_INTAKE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HomepageOnboardingIntake>;
    if (parsed.source !== "homepage_hero") return null;
    const problems = (parsed.selectedProblems ?? []).filter(isBusinessProblem);
    const channels = normalizeDeployChannels(parsed.selectedChannels ?? []);
    return {
      websiteUrl: parsed.websiteUrl?.trim() || null,
      selectedProblems: problems,
      desiredOutcomes:
        parsed.desiredOutcomes?.length
          ? parsed.desiredOutcomes
          : outcomesForProblems(problems),
      selectedChannels: channels,
      recommendedPlan:
        parsed.recommendedPlan === "associate" ||
        parsed.recommendedPlan === "professional" ||
        parsed.recommendedPlan === "intern"
          ? parsed.recommendedPlan
          : minimumPlanForChannels(channels),
      source: "homepage_hero",
      services: Array.isArray(parsed.services) ? parsed.services.filter((item) => item === "aro" || item === "aeo") : [],
      wordpressDetected: parsed.wordpressDetected === true,
      totalCents: typeof parsed.totalCents === "number" ? parsed.totalCents : null,
    };
  } catch {
    return null;
  }
}

/** Resolve intake from URL query (preferred) with sessionStorage fallback. */
export function resolveHomepageIntakeFromBrowser(): HomepageOnboardingIntake | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const problems = parseProblemsFromParam(params.get("problems"));
  const channels = parseChannelsFromParam(params.get("channels"));
  const website = params.get("website")?.trim() || null;
  const services = parsePricingServices(params.get("services"));
  const wordpressDetected = params.get("wp") === "1";
  const totalRaw = Number(params.get("totalCents"));
  const totalCents = Number.isFinite(totalRaw) ? totalRaw : null;
  if (problems.length || channels.length || services.length || wordpressDetected || totalCents != null) {
    return {
      ...buildHomepageOnboardingIntake({
        websiteUrl: website,
        selectedProblems: problems.length ? problems : [DEFAULT_HOMEPAGE_PROBLEM],
        selectedChannels: channels.length
          ? channels
          : recommendedChannelsForProblems(
              problems.length ? problems : [DEFAULT_HOMEPAGE_PROBLEM],
            ),
      }),
      services,
      wordpressDetected,
      totalCents,
    };
  }
  return readHomepageIntake();
}

export function serializeHomepageIntakeQuery(intake: HomepageOnboardingIntake): {
  problems: string;
  channels: string;
} {
  return {
    problems: serializeProblemsParam(intake.selectedProblems),
    channels: serializeChannelsParam(intake.selectedChannels),
  };
}

/** Hero starting-plan line from current channel selection (plain text for UI). */
export function formatHeroPlanRecommendationLine(channels: string[]): string {
  const summary = formatHeroPlanSummary(channels);
  return `Starting plan: ${summary.line}`;
}

/** Compact plan label for the hero Build Summary (e.g. Intern — FREE). */
export function formatHeroPlanSummary(channels: string[]): {
  planId: Exclude<AiEmployeePlanId, "enterprise">;
  name: string;
  price: string;
  line: string;
} {
  const planId = minimumPlanForChannels(channels);
  const meta = AI_EMPLOYEE_PLANS.find((p) => p.id === planId);
  const name = meta?.name ?? "Plan";
  const price = formatAiEmployeePlanPriceLabel(planId);
  return {
    planId,
    name,
    price,
    line: `${name} — ${price}`,
  };
}

/** Display labels for selected deploy channels (Browser Voice, Text Messages, etc.). */
export function channelLabelsForIds(channels: string[]): string[] {
  const normalized = normalizeDeployChannels(channels);
  return HERO_DEPLOY_CHANNEL_OPTIONS.filter((o) =>
    normalized.includes(o.id),
  ).map((o) => o.label);
}
